import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ListRequest,
  useTextblocksTextblockChangeOrderMutation,
  useTextblocksTextblockListQuery,
} from "@/services/backend/textblocks/api/textblock-api";
import { Textblock } from "@/services/backend/textblocks/types/textblock";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { GripVertical } from "lucide-react";
import { Checkbox } from "@/shared/components/ui/checkbox";
import t from "@/lang/lang";
import { EditTextblockDialog } from "./edit-textblock-dialog";
import { DeleteTextblocksDialog } from "./delete-textblocks-dialog";

export function TextblocksTable({
  request,
  selected,
  setSelected,
}: {
  request: ListRequest;
  selected: Textblock[];
  setSelected: (selected: Textblock[]) => void;
}) {
  const { data, error, isLoading, isFetching } =
    useTextblocksTextblockListQuery(request);
  const isEmpty = !isLoading && !error && data?.items.length === 0;

  const [reorderItems, { isLoading: reorderIsLoading }] =
    useTextblocksTextblockChangeOrderMutation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  async function handleDragEnd(event: DragEndEvent) {
    if (reorderIsLoading || isFetching || !data) return;

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const overItem = data.items.find((item) => item.id === over.id);
      if (!overItem) return;

      reorderItems({
        id: active.id as string,
        subdomain: overItem.subdomain,
        documentKind: overItem.documentKind,
        documentId: overItem.documentId,
        order: overItem.order,
      });
    }
  }
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={data?.items.map((i) => i.id) ?? []}
        strategy={verticalListSortingStrategy}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead />
              <TableHead>
                <Checkbox
                  checked={
                    data &&
                    data.items.length === selected.length &&
                    data.items.every((r: Textblock) =>
                      selected.some((s) => s.id === r.id),
                    )
                  }
                  onCheckedChange={(value) => {
                    if (value) {
                      setSelected(data!.items);
                    } else {
                      setSelected([]);
                    }
                  }}
                />
              </TableHead>
              <TableHead>{t("Kürzel")}</TableHead>
              <TableHead>{t("Titel")}</TableHead>
              <TableHead>{t("Text")}</TableHead>
              <TableHead className="text-right">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(isLoading || isFetching) && <TableLoadingRow colSpan={6} />}
            {error && <TableRtkErrorRow colSpan={6} error={error} />}
            {isEmpty && <TableEmptyRow colSpan={6} />}
            {data && !isFetching && (
              <TextblockRows
                items={data.items}
                selected={selected}
                setSelected={setSelected}
              />
            )}
          </TableBody>
        </Table>
      </SortableContext>
    </DndContext>
  );
}

function TextblockRows({
  items,
  selected,
  setSelected,
}: {
  items: Textblock[];
  selected: Textblock[];
  setSelected: (selected: Textblock[]) => void;
}) {
  return (
    <>
      {items.map((textblock) => (
        <TextblockRow
          key={textblock.id}
          textblock={textblock}
          selected={selected.some((r) => r.id === textblock.id)}
          setSelected={(value) => {
            if (value) {
              setSelected([...selected, textblock]);
            } else {
              setSelected(selected.filter((r) => r.id !== textblock.id));
            }
          }}
        />
      ))}
    </>
  );
}

function TextblockRow({
  textblock,
  selected,
  setSelected,
}: {
  textblock: Textblock;
  selected: boolean;
  setSelected: (value: boolean) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: textblock.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const disabled = textblock.immutable;

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell className="w-10">
        {disabled ? (
          <GripVertical className="text-muted-foreground h-4 w-4" />
        ) : (
          <button
            {...attributes}
            {...listeners}
            type="button"
            className="hover:bg-muted cursor-grab rounded p-1"
          >
            <GripVertical className="text-muted-foreground h-4 w-4" />
          </button>
        )}
      </TableCell>
      <TableCell>
        <Checkbox
          checked={selected}
          onCheckedChange={setSelected}
          disabled={disabled}
        />
      </TableCell>
      <TableCell>{textblock.shorthand}</TableCell>
      <TableCell>{textblock.title}</TableCell>
      <TableCell className="max-w-md truncate">{textblock.text}</TableCell>
      <TableCell>
        <div className="flex gap-2">
          <EditTextblockDialog textblock={textblock} />
          <DeleteTextblocksDialog selected={[textblock]} />
        </div>
      </TableCell>
    </TableRow>
  );
}
